import React, { lazy, Suspense, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import "./EDRegionalIntelligenceOverlay.css";

// Keep the interactive SVG/geographic API code out of the regular ED dashboard bundle.
const EDGeoWorkspace = lazy(() => import("../../../../geo-intelligence/executive-director/EDGeoWorkspace.jsx"));

// A chunk-loading failure must not take down the authenticated ED workspace.
class EDGeoLoadBoundary extends React.Component {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  render() {
    if (this.state.failed) return <div className="edGeoIntelligenceLoading" role="alert">
      <span>The regional map could not be opened. Please retry from the dashboard.</span>
      <button type="button" autoFocus onClick={this.props.onClose}>Close map</button>
    </div>;
    return this.props.children;
  }
}

export default function EDRegionalIntelligenceOverlay({ onClose, returnFocusTo }) {
  const dialog = useRef(null);
  const opener = useRef(null);

  useEffect(() => {
    opener.current = returnFocusTo?.isConnected ? returnFocusTo : document.activeElement;
    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    dialog.current?.focus();
    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
      // Wait until the ED workspace is no longer inert before returning focus.
      window.requestAnimationFrame(() => {
        if (opener.current?.isConnected) opener.current.focus();
      });
    };
  }, []);

  const handleKeyDown = event => {
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
      event.preventDefault();
      event.stopPropagation();
      dialog.current?.querySelector("#ugi-global-input")?.focus();
      return;
    }
    if (event.key === "Escape" && !dialog.current?.querySelector(".ugi-shell")) {
      // Escape works even while the geographical component is loading.
      event.stopPropagation();
      onClose();
      return;
    }
    if (event.key !== "Tab" || dialog.current?.querySelector('.ugi-detail[role="dialog"]')) return;
    const controls = Array.from(dialog.current?.querySelectorAll(
      'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    ) || []).filter(element => element.getClientRects().length && !element.closest('[aria-hidden="true"]'));
    if (!controls.length) { event.preventDefault(); dialog.current?.focus(); return; }
    const first = controls[0];
    const last = controls[controls.length - 1];
    if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog.current)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return createPortal(
    <div ref={dialog} className="edGeoIntelligenceDialog" role="dialog" aria-modal="true"
      aria-label="Executive Director Regional Intelligence" tabIndex={-1} onKeyDown={handleKeyDown}>
      <EDGeoLoadBoundary onClose={onClose}>
        <Suspense fallback={<div className="edGeoIntelligenceLoading" role="status">Opening Executive Director geographical intelligence…</div>}>
          <EDGeoWorkspace onClose={onClose} initialView="nigeria" focusCloseOnMount />
        </Suspense>
      </EDGeoLoadBoundary>
    </div>,
    document.body
  );
}
